'use client';

import { ReactLenis } from 'lenis/react';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

interface SmoothScrollProviderProps {
  children: ReactNode;
}

/**
 * 整站平滑惯性滚动，对标 kudanil.com 的核心手感（Lenis root 模式，接管 document 滚动）。
 *
 * 仅用于首页 sections 外层，不污染 workspace / 其它路由的原生滚动。
 *
 * 降级与兼容：
 * - prefers-reduced-motion：不启用 Lenis，退回原生滚动（无障碍）。
 * - 触屏设备：不接管（smoothTouch 默认关闭），保留系统原生惯性，避免移动端手感异常。
 * - 需保留原生滚动的内部区域（如 WhyVidu 的 onWheel 劫持区、弹窗滚动区），
 *   在该元素上加 `data-lenis-prevent`，Lenis 会自动跳过。
 */
export const SmoothScrollProvider = ({ children }: SmoothScrollProviderProps) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // 粗指针（触屏）设备保留原生滚动
    const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
    setEnabled(!reduceMotion && !coarsePointer);
  }, []);

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <ReactLenis
      root
      options={{
        // kudanil 那种「重、顺、跟手」的惯性：稍长的缓动 + 适中阻尼
        duration: 1.1,
        easing: (t: number) => Math.min(1, 1.001 - 2 ** (-10 * t)),
        smoothWheel: true,
        wheelMultiplier: 1,
        touchMultiplier: 1.5,
      }}
    >
      {children}
    </ReactLenis>
  );
};
